// Utility functions for UpdatePaymentPlan component
import {
  DataPaymentState,
  AddressValue,
  Transaction,
  CardPayload,
} from "types/updatePaymentPlan";
import { checkDefaultMethodExist, getMethod } from "utils/HelperFunctions";
import { FormattedPaymentMethodTypes } from "types/paymentMethodComponent";
import { valuesProps } from "types/paymentMethods";
import moment from "moment";

const firstPopulatedAddressValue = (...values: unknown[]): string => {
  const value = values.find(
    (candidate) =>
      candidate !== null &&
      candidate !== undefined &&
      `${candidate}`.trim().length > 0
  );

  return value === undefined ? "" : `${value}`.trim();
};

/**
 * The plan-update endpoint persists these aliases onto clientCustomer's
 * non-null billing columns. New-card forms may collect only a postal code, so
 * complete the address from the saved method, plan, or account instead of
 * sending undefined values that Sequelize converts to null.
 */
export const getUpdateBillingAddress = (
  formValues: any = {},
  paymentMethod: any = {},
  clientCustomer: any = {},
  accountData: any = {}
) => ({
  x_city: firstPopulatedAddressValue(
    formValues?.x_city,
    paymentMethod?.billing_city,
    clientCustomer?.billing_city,
    accountData?.city
  ),
  x_address: firstPopulatedAddressValue(
    formValues?.x_address,
    paymentMethod?.billing_address,
    paymentMethod?.billing_address1,
    clientCustomer?.billing_address1,
    accountData?.address1
  ),
  address2: firstPopulatedAddressValue(
    formValues?.address2,
    paymentMethod?.billing_address2,
    clientCustomer?.billing_address2,
    accountData?.address2
  ),
  x_zip: firstPopulatedAddressValue(
    formValues?.x_zip,
    paymentMethod?.billing_zip,
    clientCustomer?.billing_zip,
    accountData?.postal_code
  ),
  x_state: firstPopulatedAddressValue(
    formValues?.x_state,
    paymentMethod?.billing_state,
    clientCustomer?.billing_state,
    accountData?.state
  ),
  x_country: firstPopulatedAddressValue(
    formValues?.x_country,
    paymentMethod?.billing_country,
    clientCustomer?.billing_country,
    accountData?.country
  ),
});

export const getOperatingSystem = (): string => {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iosPlatforms = ["iPhone", "iPad", "iPod"];
  let os: string | null = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "macOS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (!os && /Linux/.test(platform)) {
    os = "Linux";
  } else if (/CrOS/.test(userAgent)) {
    os = "Chrome OS";
  } else {
    os = "Other";
  }

  return os;
};

/**
 * Get address values based on payment method
 */
const getAddressValue = (payload: any, accountData: any): AddressValue => {
  if (payload?.x_payment_method === "CC") {
    return {
      x_city: payload?.x_city,
      x_address: payload?.x_address,
      address2: payload?.address2,
      x_zip: payload?.x_zip,
      x_state: payload?.x_state,
      x_country: payload?.x_country,
    };
  } else {
    return {
      x_city: accountData?.city,
      x_address: accountData?.address1,
      address2: accountData?.address2,
      x_zip: accountData?.postal_code,
      x_state: accountData?.state,
      x_country: accountData?.country,
    };
  }
};

/**
 * Create a card payload for payment processing
 */
const createCardPayload = (
  values: valuesProps,
  accountData: any,
  accountLeadData: any,
  cardList: FormattedPaymentMethodTypes[],
  bankList: FormattedPaymentMethodTypes[],
  oldWalletData: any,
  paymentPermission: any,
  isAddNewCard: boolean,
  dataPayment: DataPaymentState,
  selectedMethod: string
): CardPayload => {
  const { x_city, x_address, address2, x_zip, x_state, x_country } =
    getAddressValue(values, accountData);
  const method = getMethod(isAddNewCard, dataPayment, selectedMethod);
  const defaultValue =
    checkDefaultMethodExist(cardList, bankList, oldWalletData) === "Y";

  return {
    x_card_num: values?.x_card_num?.replaceAll(" ", "") || "",
    x_exp_date: values?.x_exp_date || "",
    x_card_code: values?.x_card_code || "",
    choldername: values?.choldername || "",
    x_payment_method: method || "",
    x_city,
    x_address,
    address2,
    x_zip,
    x_state,
    x_country,
    routing_number: values?.routing_number,
    account_number: values?.account_number,
    bank_name: values?.bank_name,
    swift_code: values?.swift_code,
    card_funding_type: values?.card_funding_type,
    default: defaultValue,
    default_profile: defaultValue,
    cluster_id: accountData?.clusterid,
    accountid: accountData?.accountid,
    x_first_name: accountData?.first_name,
    x_last_name: accountData?.last_name,
    x_phone: accountData?.phone || "",
    x_email: accountData?.email || "",
    lead_id: accountLeadData?.lead_id || "",
    gateway_id:
      method === "ACH"
        ? paymentPermission?.ach_gateway
        : paymentPermission?.cc_gateway,
    gateway_name:
      method === "ACH"
        ? paymentPermission?.ach_gateway_name?.name
        : paymentPermission?.cc_gateway_name?.name,
    account_holder_name: values?.choldername ?? "",
    card_type: values?.card_type,
    accounts_id: accountData?.id,
  };
};

/**
 * Create formatted transaction list for payment update
 */
export const isPendingScheduleTransaction = (transaction: Transaction) =>
  `${transaction?.status || ""}`.trim().toLowerCase() === "pending";

const PAYMENT_PLAN_DATE_FORMATS = [
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "M/D/YYYY",
  moment.ISO_8601,
];

/**
 * Payment-plan dates are date-only values. Parse the formats used by the
 * existing editor explicitly so serialization never depends on the browser's
 * interpretation of MM/DD/YYYY.
 */
export const formatPaymentPlanBillingDate = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";

  const parsed = moment.isMoment(value)
    ? value.clone()
    : value instanceof Date
      ? moment(value)
      : moment(`${value}`.trim(), PAYMENT_PLAN_DATE_FORMATS, true);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : `${value}`.trim();
};

const normalizeComparableScheduleAmount = (value: any): string => {
  const normalized = `${value ?? ""}`.replace(/[$,]/g, "").trim();
  if (!normalized) return "";

  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : normalized;
};

export type PaymentPlanScheduleSnapshot = {
  frequency: string;
  scheduleAmount: string;
  transactionType: string;
  transactionCount: number;
  pendingTransactions: Array<{
    id: string;
    amountType: string;
    amount: string;
    percentage: string;
    billingDate: string;
  }>;
};

export const createPaymentPlanScheduleSnapshot = (
  paymentPlan: any = {}
): PaymentPlanScheduleSnapshot => {
  const transactions: Transaction[] = Array.isArray(paymentPlan?.transactions)
    ? paymentPlan.transactions
    : [];

  return {
    frequency: `${
      paymentPlan?.schedule_fld ?? paymentPlan?.payment_schedule ?? ""
    }`
      .trim()
      .toLowerCase(),
    scheduleAmount: `${paymentPlan?.schedule_amount ?? ""}`
      .trim()
      .toLowerCase(),
    transactionType: `${paymentPlan?.transaction_type ?? ""}`
      .trim()
      .toLowerCase(),
    transactionCount: getScheduleTransactionCount(paymentPlan),
    pendingTransactions: transactions
      .filter(isPendingScheduleTransaction)
      .map((transaction) => ({
        id: `${transaction?.id ?? ""}`,
        amountType: `${transaction?.amount_type ?? ""}`.trim().toUpperCase(),
        amount: normalizeComparableScheduleAmount(transaction?.amount),
        percentage: normalizeComparableScheduleAmount(transaction?.percentage),
        billingDate: formatPaymentPlanBillingDate(
          getTransactionScheduleDate(transaction)
        ),
      })),
  };
};

export const hasPaymentPlanScheduleChanged = (
  initialSnapshot: PaymentPlanScheduleSnapshot | null | undefined,
  paymentPlan: any
): boolean =>
  Boolean(initialSnapshot) &&
  JSON.stringify(initialSnapshot) !==
    JSON.stringify(createPaymentPlanScheduleSnapshot(paymentPlan));

export type PaymentMethodSelection = {
  method: string;
  paymentMethodId?: string | number | null;
  isNew?: boolean;
};

const normalizePaymentMethodCode = (method = ""): string => {
  const normalized = `${method}`.trim().toUpperCase();
  const methodMap: Record<string, string> = {
    CARD: "CC",
    BANK: "ACH",
    PAYPAL: "PP",
    "CASH APP PAY": "CAPP",
    "AMAZON PAY": "AMZP",
    "GOOGLE PAY": "GP",
    "APPLE PAY": "AP",
    WIRE: "WTO",
  };

  return methodMap[normalized] || normalized;
};

export const createPaymentMethodSelection = (
  method: string,
  paymentMethodId?: string | number | null,
  isNew = false
): PaymentMethodSelection => ({
  method: normalizePaymentMethodCode(method),
  paymentMethodId:
    paymentMethodId === null || paymentMethodId === undefined
      ? undefined
      : `${paymentMethodId}`,
  isNew,
});

export const hasPaymentMethodChanged = (
  initialSelection: PaymentMethodSelection | null | undefined,
  userSelection: PaymentMethodSelection | null | undefined
): boolean => {
  if (!userSelection) return false;
  if (userSelection.isNew) return true;
  if (!initialSelection) return true;
  if (initialSelection.method !== userSelection.method) return true;

  const initialId = `${initialSelection.paymentMethodId ?? ""}`;
  const selectedId = `${userSelection.paymentMethodId ?? ""}`;

  // A user-picked saved method is a real change unless it can be positively
  // matched to the method already attached to the plan.
  return !initialId || !selectedId || initialId !== selectedId;
};

export const getPaymentPlanUpdateFlags = (
  initialSchedule: PaymentPlanScheduleSnapshot | null | undefined,
  currentPaymentPlan: any,
  initialMethod: PaymentMethodSelection | null | undefined,
  currentMethod: PaymentMethodSelection | null | undefined
) => ({
  updateMethod: hasPaymentMethodChanged(initialMethod, currentMethod),
  updateSchedule: hasPaymentPlanScheduleChanged(
    initialSchedule,
    currentPaymentPlan
  ),
});

export const shouldDisablePaymentPlanUpdate = ({
  flags,
  isRetry = false,
  isSubmitting = false,
  isScheduleBusy = false,
  hasScheduleError = false,
  hasPaymentMethodError = false,
}: {
  flags: { updateMethod: boolean; updateSchedule: boolean };
  isRetry?: boolean;
  isSubmitting?: boolean;
  isScheduleBusy?: boolean;
  hasScheduleError?: boolean;
  hasPaymentMethodError?: boolean;
}): boolean =>
  (!flags.updateMethod && !flags.updateSchedule && !isRetry) ||
  isSubmitting ||
  isScheduleBusy ||
  hasScheduleError ||
  (flags.updateMethod && hasPaymentMethodError);

export const runPaymentPlanUpdateOnce = async <T>(
  inFlight: { current: boolean },
  request: () => Promise<T>
): Promise<T | null> => {
  if (inFlight.current) return null;

  inFlight.current = true;
  try {
    return await request();
  } finally {
    inFlight.current = false;
  }
};

export const createFormattedTransactionList = (
  transactions: Transaction[],
  clusterid: string | number,
  lead_owner: string,
  paymentMethod = ""
): Array<{
  id?: string | number;
  amount_type: string;
  amount: number | string;
  billing_date: string;
  clusterid: string | number;
  lead_owner?: string;
  cf_amount?: number | string;
  percentage: string;
}> => {
  const newList = transactions.filter((transaction: Transaction) =>
    isPendingScheduleTransaction(transaction)
  );

  return newList.map((transaction: Transaction) => ({
    ...(transaction?.id !== null &&
    transaction?.id !== undefined &&
    transaction?.id !== ""
      ? { id: transaction.id }
      : {}),
    amount_type: transaction?.amount_type || "",
    amount: transaction?.amount ?? 0,
    billing_date: formatPaymentPlanBillingDate(
      getTransactionScheduleDate(transaction)
    ),
    clusterid,
    lead_owner,
    cf_amount: getTransactionFeeForPaymentMethod(transaction, paymentMethod),
    percentage: "",
  }));
};

export const createPaymentPlanUpdateFields = ({
  paymentPlan,
  clusterid,
  leadOwner,
  paymentMethod,
  flags,
}: {
  paymentPlan: any;
  clusterid: string | number;
  leadOwner: string;
  paymentMethod?: string;
  flags: { updateMethod: boolean; updateSchedule: boolean };
}) => ({
  payment_schedule: paymentPlan?.schedule_fld || "",
  created_array: createFormattedTransactionList(
    paymentPlan?.transactions || [],
    clusterid,
    leadOwner,
    paymentMethod || ""
  ),
  updateMethod: flags.updateMethod,
  updateSchedule: flags.updateSchedule,
});

/**
 * Check if payment method requires address
 */
const requiresAddress = (method: string): boolean => {
  return ["CC", "GP", "AP"].includes(method);
};

/**
 * Get pending transaction count for today
 */
const getTodayPendingTransactionCount = (
  transactions: Transaction[]
): number => {
  const moment = require("moment");
  return transactions.filter((transaction: Transaction) =>
    moment(
      transaction?.date ||
        transaction?.payment_date ||
        transaction?.billing_date
    ).isSame(moment(), "day")
  ).length;
};

/**
 * Validate if should process payment
 */
const shouldProcessPayment = (
  checkedDuplicatePayment: boolean,
  isDuplicate: boolean
): boolean => {
  return checkedDuplicatePayment || !isDuplicate;
};

export const getTransactionScheduleDate = (transaction: any): string =>
  transaction?.billing_date ||
  transaction?.payment_date ||
  transaction?.date ||
  "";

export const updateTransactionScheduleDate = <T extends object>(
  transaction: T,
  value: string
): T & { billing_date: string; payment_date: string; date: string } => ({
  ...transaction,
  billing_date: value,
  payment_date: value,
  date: value,
});

export const getTransactionConvenienceFee = (transaction: any): number => {
  const fee =
    transaction?.fee ?? transaction?.cf_amount ?? transaction?.con_fee;
  const numericFee = Number(fee);

  return Number.isFinite(numericFee) ? numericFee : 0;
};

const isPayPalPaymentMethod = (paymentMethod = ""): boolean =>
  ["PP", "PAYPAL"].includes(`${paymentMethod}`.trim().toUpperCase());

/**
 * Processed rows are historical accounting records and must keep the fee that
 * was actually charged. Pending PayPal rows are future payments, so an old
 * card fee must never be carried into their preview or update payload.
 *
 * The server remains responsible for authoritatively pricing every method.
 * This client-side rule prevents a known stale card fee from being submitted
 * while preserving the original fee on completed/failed historical rows.
 */
export const getTransactionFeeForPaymentMethod = (
  transaction: Transaction,
  paymentMethod = ""
): number =>
  isPayPalPaymentMethod(paymentMethod) &&
  isPendingScheduleTransaction(transaction)
    ? 0
    : getTransactionConvenienceFee(transaction);

export const normalizeScheduleFeesForPaymentMethod = (
  transactions: Transaction[] = [],
  paymentMethod = ""
): Transaction[] =>
  transactions.map((transaction) => {
    if (
      !isPayPalPaymentMethod(paymentMethod) ||
      !isPendingScheduleTransaction(transaction)
    ) {
      return { ...transaction };
    }

    return {
      ...transaction,
      fee: 0,
      cf_amount: 0,
      con_fee: 0,
    };
  });

export const getScheduleEndDateLimit = (
  limit: string | null | undefined,
  now: Date = new Date()
): Date => {
  const [rawValue, rawUnit] = `${limit || ""}`.split("-");
  const value = Number(rawValue);
  const unit = rawUnit?.toUpperCase();

  if (!Number.isFinite(value) || value < 0 || !["D", "W", "M"].includes(unit)) {
    return moment(now).add(1, "year").toDate();
  }

  const momentUnit = unit === "M" ? "months" : unit === "W" ? "weeks" : "days";

  return moment(now).add(value, momentUnit).toDate();
};

export const getScheduleEndDateError = (responseBody: any) => {
  const responseData = responseBody?.data || {};

  return {
    isInvalid: Boolean(responseData?.isInvalidEndDate),
    message:
      responseBody?.message ||
      responseData?.message ||
      responseData?.msg ||
      null,
  };
};

/**
 * A row added in Custom mode starts with no date but is already counted in the
 * totals, and the schedule endpoint accepts it, so the blank has to be caught
 * here before it becomes a real installment.
 */
export const MISSING_SCHEDULE_DATE_MESSAGE =
  "Please select a date for every payment";

export const getMissingScheduleDateError = (
  transactions: any[] = []
): string | null => {
  const hasBlankDate = transactions.some((transaction: any) => {
    if (transaction?.status && transaction.status !== "Pending") return false;
    return !getTransactionScheduleDate(transaction);
  });

  return hasBlankDate ? MISSING_SCHEDULE_DATE_MESSAGE : null;
};

const DEFAULT_DECLINE_REASON = "Payment failed";
const DEFAULT_UPDATE_ERROR_REASON =
  "We couldn't update your payment plan. Please try again.";
const SCHEDULE_UPDATE_ERROR_REASON =
  "We couldn't update the payment schedule. Review the schedule and try again.";
const BILLING_ADDRESS_ERROR_REASON =
  "The billing address is incomplete. Review your payment method details and try again.";
const DECLINE_ERROR_CODES = new Set([
  "CARD_DECLINED",
  "PAYMENT_DECLINED",
  "PAYMENT_METHOD_DECLINED",
  "DO_NOT_HONOR",
]);

const getUpdateErrorCode = (responseBody: any): string =>
  `${
    responseBody?.code ||
    responseBody?.error_code ||
    responseBody?.data?.code ||
    responseBody?.data?.error_code ||
    ""
  }`
    .trim()
    .toUpperCase();

/**
 * A failed update has two very different causes: the gateway declined the
 * charge, or the request never got that far (validation, auth, server error).
 * Only the first one is the customer's card, so only that one may claim it is.
 * Server-side technical detail is never rendered.
 */
export const getUpdatePlanFailure = (
  responseBody: any,
  error?: unknown
): { reason: string; isDeclined: boolean } | null => {
  if (error) {
    return { reason: DEFAULT_UPDATE_ERROR_REASON, isDeclined: false };
  }

  const errorCode = getUpdateErrorCode(responseBody);
  const errorMessage = `${responseBody?.message || ""}`.toLowerCase();

  if (DECLINE_ERROR_CODES.has(errorCode)) {
    return { reason: DEFAULT_DECLINE_REASON, isDeclined: true };
  }

  // success === false means the plan update itself was rejected, not a card.
  if (!responseBody || responseBody.success === false) {
    const hasBillingAddressValidationError =
      errorMessage.includes("notnull violation") &&
      ["billing_city", "billing_zip", "billing_address1"].some((field) =>
        errorMessage.includes(field)
      );

    return {
      reason: hasBillingAddressValidationError
        ? BILLING_ADDRESS_ERROR_REASON
        : errorCode.includes("SCHEDULE")
          ? SCHEDULE_UPDATE_ERROR_REASON
          : DEFAULT_UPDATE_ERROR_REASON,
      isDeclined: false,
    };
  }

  const paymentResponses: any[] = Array.isArray(
    responseBody?.data?.final_payments_response
  )
    ? responseBody.data.final_payments_response
    : [];

  // The plan can save while a charge inside it is refused, so an unapproved
  // entry still has to surface as a failure rather than a success screen.
  const declined = paymentResponses.find(
    (payment: any) =>
      payment?.approved !== undefined && Number(payment.approved) !== 1
  );

  if (declined) {
    return {
      reason: declined?.msg || declined?.message || DEFAULT_DECLINE_REASON,
      isDeclined: true,
    };
  }

  return null;
};

const getFiniteNumber = (value: any): number => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const isPercentageSchedule = (
  transactionType = "",
  transactions: Transaction[] = []
): boolean => {
  const normalizedType = `${transactionType}`.trim().toLowerCase();

  return (
    ["p", "percent", "percentage"].includes(normalizedType) ||
    `${transactions[0]?.amount_type || ""}`.trim().toUpperCase() === "P"
  );
};

/**
 * The schedule endpoint expects the requested total number of rows, including
 * installments that have already been processed. A smaller user selection must
 * be allowed to trim future pending rows, while rows that have already been
 * processed can never be removed.
 */
export const getScheduleTransactionCount = (paymentPlan: any): number => {
  const configuredCount = Math.max(
    0,
    Math.floor(getFiniteNumber(paymentPlan?.no_of_transaction))
  );
  const transactions: Transaction[] = Array.isArray(paymentPlan?.transactions)
    ? paymentPlan.transactions
    : [];
  const minimumLockedCount = transactions.reduce(
    (minimumCount, transaction, index) =>
      isPendingScheduleTransaction(transaction)
        ? minimumCount
        : Math.max(minimumCount, index + 1),
    0
  );

  return configuredCount > 0
    ? Math.max(configuredCount, minimumLockedCount)
    : transactions.length;
};

/**
 * Keep schedule requests anchored to the plan's original first installment.
 * The update flow also tracks the first pending date, but that date must not
 * replace the schedule start after an earlier installment has succeeded.
 */
export const getPaymentPlanStartDate = (
  paymentPlan: any,
  fallbackDate = ""
): string => {
  const startDate =
    paymentPlan?.start_date ||
    getTransactionScheduleDate(paymentPlan?.transactions?.[0]) ||
    fallbackDate;
  const parsedDate = moment(startDate);

  return parsedDate.isValid() ? parsedDate.format("MM/DD/YYYY") : fallbackDate;
};

/**
 * Some schedule responses contain every row while older responses contain only
 * pending rows. Select by schedule index for a full response and by pending-row
 * index for a pending-only response.
 */
export const getScheduleResponseTransaction = (
  responseTransactions: Transaction[] = [],
  transactionIndex = 0,
  pendingIndex = 0,
  targetTransactionCount = 0
): Transaction => {
  const includesFullSchedule =
    targetTransactionCount > 0 &&
    responseTransactions.length >= targetTransactionCount;

  return (
    responseTransactions[
      includesFullSchedule ? transactionIndex : pendingIndex
    ] || {}
  );
};

type ReconcileUpdatePlanScheduleOptions = {
  amountTypeCode: string;
  preserveCustomDates?: boolean;
  scheduleAmountType: string;
};

export const isLatestScheduleRequest = (
  activeRequestId: number,
  latestRequestId: number
): boolean => activeRequestId === latestRequestId;

/**
 * Reconcile regenerated schedule data to the exact requested row count.
 * Processed rows and existing editable values are preserved, removed pending
 * rows are truncated, and newly added rows receive generated dates and zeroed
 * custom values.
 */
export const reconcileUpdatePlanScheduleTransactions = (
  transactions: Transaction[] = [],
  responseTransactions: Transaction[] = [],
  targetTransactionCount = 0,
  {
    amountTypeCode,
    preserveCustomDates = false,
    scheduleAmountType,
  }: ReconcileUpdatePlanScheduleOptions
): Transaction[] => {
  const safeTargetCount = Math.max(
    0,
    Math.floor(getFiniteNumber(targetTransactionCount))
  );
  const normalizedScheduleAmountType = `${scheduleAmountType || ""}`
    .trim()
    .toLowerCase();
  let pendingIndex = 0;

  return Array.from(
    { length: safeTargetCount },
    (_, transactionIndex): Transaction => {
      const existingTransaction = transactions[transactionIndex];

      if (
        existingTransaction &&
        !isPendingScheduleTransaction(existingTransaction)
      ) {
        return { ...existingTransaction };
      }

      const generatedTransaction = getScheduleResponseTransaction(
        responseTransactions,
        transactionIndex,
        pendingIndex,
        safeTargetCount
      );
      pendingIndex += 1;

      const amount =
        normalizedScheduleAmountType === "custom"
          ? (existingTransaction?.amount ??
            generatedTransaction?.amount ??
            "0.00")
          : (generatedTransaction?.amount ?? existingTransaction?.amount ?? 0);
      const date = preserveCustomDates
        ? getTransactionScheduleDate(existingTransaction)
        : getTransactionScheduleDate(generatedTransaction) ||
          getTransactionScheduleDate(existingTransaction);
      const fee =
        generatedTransaction?.con_fee ??
        generatedTransaction?.fee ??
        existingTransaction?.fee ??
        existingTransaction?.cf_amount ??
        0;

      return updateTransactionScheduleDate(
        {
          ...(existingTransaction || {}),
          amount,
          amount_type: amountTypeCode,
          balance:
            generatedTransaction?.balance ?? existingTransaction?.balance ?? 0,
          fee,
          cf_amount: getFiniteNumber(fee),
          status: "Pending",
        },
        date
      );
    }
  );
};

/**
 * Percentage rows store the selected percentage in `amount`, while previews
 * must show and total the corresponding scheduled dollar amount.
 */
export const getScheduleDisplayAmount = (
  transaction: Transaction,
  index: number,
  percentageAmounts: Array<number | string> = [],
  transactionType = "",
  transactions: Transaction[] = []
): number => {
  if (!isPercentageSchedule(transactionType, transactions)) {
    return getFiniteNumber(transaction?.amount);
  }

  const percentageAmount = percentageAmounts[index];
  if (
    percentageAmount !== undefined &&
    percentageAmount !== null &&
    percentageAmount !== ""
  ) {
    return getFiniteNumber(percentageAmount);
  }

  return getFiniteNumber(transaction?.amount);
};

const getScheduleTimestamp = (transaction: any): number => {
  const value = getTransactionScheduleDate(transaction);
  if (!value) return Number.POSITIVE_INFINITY;

  const mmddyyyy = `${value}`.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyy) {
    const [, month, day, year] = mmddyyyy;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
};

export const sortDates = <T>(dates: T[] = []): T[] =>
  [...dates].sort(
    (a: T, b: T) => getScheduleTimestamp(a) - getScheduleTimestamp(b)
  );

/**
 * Convert the update endpoint's legacy transaction shape into one canonical
 * in-memory shape. Keeping all three date aliases synchronized prevents the UI
 * and submission serializer from selecting different dates for the same row.
 */
export const normalizeUpdatePlanTransactions = (
  transactions: Transaction[] = []
): Transaction[] =>
  sortDates(
    transactions.map((transaction) => {
      const amount =
        `${transaction?.amount_type || ""}`.trim().toUpperCase() === "P"
          ? (transaction?.percentage ?? transaction?.amount)
          : transaction?.amount;

      return updateTransactionScheduleDate(
        { ...transaction, amount },
        getTransactionScheduleDate(transaction)
      );
    })
  );

/**
 * A plan-level amount can represent the full plan balance. The fixed amount
 * control must therefore be initialized from an editable installment row.
 */
export const getFixedScheduleInstallmentAmount = (
  transactions: Transaction[] = []
): number | string => {
  const editableTransaction = transactions.find(isPendingScheduleTransaction);

  return editableTransaction?.amount ?? transactions[0]?.amount ?? 0;
};

/**
 * Merge regenerated dates into a custom schedule without allowing the
 * schedule API to replace amounts or percentages entered by the user.
 * Existing rows keep their values, removed rows are truncated, and rows added
 * by increasing the transaction count start at zero.
 */
export const mergeGeneratedCustomSchedule = (
  transactions: Transaction[] = [],
  generatedTransactions: Transaction[] = [],
  targetTransactionCount = 0,
  transactionType = "amount"
): Transaction[] => {
  const safeTargetCount = Math.max(
    0,
    Math.floor(getFiniteNumber(targetTransactionCount))
  );
  const baseTransaction = transactions[0] || {};
  const normalizedTransactionType = `${transactionType}`.trim().toLowerCase();
  const defaultAmountType = ["p", "percent", "percentage"].includes(
    normalizedTransactionType
  )
    ? "P"
    : "A";

  return Array.from({ length: safeTargetCount }, (_, index) => {
    const currentTransaction = transactions[index];
    const generatedTransaction = generatedTransactions[index] || {};
    const nextDate =
      getTransactionScheduleDate(generatedTransaction) ||
      getTransactionScheduleDate(currentTransaction) ||
      "";

    return updateTransactionScheduleDate(
      {
        ...(currentTransaction || baseTransaction),
        id: null,
        amount: currentTransaction?.amount ?? 0,
        amount_type: currentTransaction?.amount_type || defaultAmountType,
        balance:
          generatedTransaction?.balance ?? currentTransaction?.balance ?? 0,
        fee: generatedTransaction?.fee ?? currentTransaction?.fee ?? 0,
        status: currentTransaction?.status || "Pending",
      },
      nextDate
    );
  });
};

export const calculateScheduleTotals = (transactions: Transaction[] = []) => {
  const amountSubtotal = transactions.reduce(
    (total, transaction) => total + getFiniteNumber(transaction?.amount),
    0
  );
  const feeSubtotal = transactions.reduce(
    (total, transaction) => total + getTransactionConvenienceFee(transaction),
    0
  );

  return {
    amountSubtotal,
    feeSubtotal,
    grandTotal: amountSubtotal + feeSubtotal,
  };
};

/**
 * Re-split the unpaid balance after an auto-schedule row is added or removed.
 * Integer cents keep the installments and displayed subtotal exactly aligned.
 */
export const redistributePendingScheduleAmounts = (
  transactions: Transaction[] = [],
  totalBalance = 0
): Transaction[] => {
  const totalCents = Math.round(getFiniteNumber(totalBalance) * 100);
  const pendingCount = transactions.filter(
    (transaction) => transaction?.status === "Pending"
  ).length;

  if (!pendingCount) {
    return calculateScheduleSummary(transactions, totalBalance, "amount")
      .dataArr;
  }

  const processedCents = transactions.reduce((total, transaction) => {
    if (transaction?.status === "Pending") return total;
    return total + Math.round(getFiniteNumber(transaction?.amount) * 100);
  }, 0);
  const remainingCents = Math.max(totalCents - processedCents, 0);
  const amountPerPendingRow = Math.floor(remainingCents / pendingCount);
  let remainder = remainingCents % pendingCount;
  let outstandingCents = totalCents;

  return transactions.map((transaction) => {
    const amountCents =
      transaction?.status === "Pending"
        ? amountPerPendingRow + (remainder-- > 0 ? 1 : 0)
        : Math.round(getFiniteNumber(transaction?.amount) * 100);

    outstandingCents -= amountCents;

    return {
      ...transaction,
      amount: (amountCents / 100).toFixed(2),
      balance: outstandingCents / 100,
    };
  });
};

export const calculateScheduleSummary = (
  transactions: Transaction[] = [],
  totalBalance = 0,
  transactionType = ""
) => {
  const isPercentage = isPercentageSchedule(transactionType, transactions);
  const balance = getFiniteNumber(totalBalance);
  let outstanding = balance;
  let amountSubtotal = 0;
  const percentageAmounts: number[] = [];

  const dataArr = transactions.map((transaction) => {
    const scheduledAmount = isPercentage
      ? (balance * getFiniteNumber(transaction?.amount)) / 100
      : getFiniteNumber(transaction?.amount);

    amountSubtotal += scheduledAmount;
    outstanding -= scheduledAmount;
    percentageAmounts.push(scheduledAmount);

    return {
      ...transaction,
      balance: outstanding,
    };
  });
  const feeSubtotal = transactions.reduce(
    (total, transaction) => total + getTransactionConvenienceFee(transaction),
    0
  );

  return {
    dataArr,
    amountSubtotal,
    feeSubtotal,
    grandTotal: amountSubtotal + feeSubtotal,
    percentageAmounts: isPercentage ? percentageAmounts : [],
  };
};

const getResponseNumber = (...values: any[]): number | undefined => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numericValue = Number(`${value}`.replace(/[$,]/g, ""));
    if (Number.isFinite(numericValue)) return numericValue;
  }

  return undefined;
};

/**
 * The update endpoint owns the final dates, fees and totals. Use its schedule
 * verbatim after a successful update, with local calculations only as a
 * fallback for older responses that omit summary fields.
 */
export const getAuthoritativePaymentPlanState = (
  currentPaymentPlan: any = {},
  responseData: any = {},
  totalBalance = 0
) => {
  if (!Array.isArray(responseData?.date_array)) return currentPaymentPlan;

  const transactions = normalizeUpdatePlanTransactions(responseData.date_array);
  const transactionType =
    responseData?.transaction_type ||
    currentPaymentPlan?.transaction_type ||
    (transactions[0]?.amount_type === "P" ? "percent" : "amount");
  const summary = calculateScheduleSummary(
    transactions,
    totalBalance,
    transactionType
  );
  const amountSubtotal = getResponseNumber(
    responseData?.amount_subtotal,
    responseData?.amount_total,
    responseData?.subtotal
  );
  const feeSubtotal = getResponseNumber(
    responseData?.fee_subtotal,
    responseData?.total_fee
  );
  const grandTotal = getResponseNumber(
    responseData?.grand_total,
    responseData?.total_amount,
    responseData?.total
  );
  const remainingPayments =
    getResponseNumber(responseData?.remaining_payments) ??
    transactions.filter(isPendingScheduleTransaction).length;
  const nextPendingTransaction = transactions.find(
    isPendingScheduleTransaction
  );

  return {
    ...currentPaymentPlan,
    transactions,
    amount_subtotal: amountSubtotal ?? summary.amountSubtotal,
    fee_subtotal: feeSubtotal ?? summary.feeSubtotal,
    grand_total:
      grandTotal ??
      (amountSubtotal ?? summary.amountSubtotal) +
        (feeSubtotal ?? summary.feeSubtotal),
    no_of_transaction:
      getResponseNumber(responseData?.no_of_transaction) ?? transactions.length,
    payment_schedule:
      responseData?.payment_schedule ||
      responseData?.schedule_fld ||
      currentPaymentPlan?.payment_schedule,
    schedule_fld:
      responseData?.schedule_fld ||
      responseData?.payment_schedule ||
      currentPaymentPlan?.schedule_fld,
    schedule_amount:
      responseData?.schedule_amount || currentPaymentPlan?.schedule_amount,
    transaction_type: transactionType,
    percentage_amounts: summary.percentageAmounts,
    remaining_payments: remainingPayments,
    next_billing_date:
      responseData?.next_billing_date ||
      getTransactionScheduleDate(nextPendingTransaction),
  };
};
